'use client';

import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import { useState } from 'react';
import AuthShell from '@/components/auth/AuthShell';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { authRequest, safeNext } from '@/lib/auth/client';
import { SIGNIN_FLOW } from '@/lib/auth/challenge';

export default function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = safeNext(searchParams.get('next'));
  const [form, setForm] = useState({ email: '', password: '' });
  /**
   * Unchecked by default — IN-336.
   *
   * It decides whether the session cookie lives eight hours or thirty days,
   * and pre-ticking it made that choice on the customer's behalf, silently
   * and in the direction that suits us rather than them. A shared or
   * borrowed machine is the case that matters: the person who most needs
   * the short session is the least likely to notice a box that is already
   * ticked.
   *
   * It also brings the screen in line with the server, where
   * `setSessionCookies` has always defaulted `remember` to false. The
   * checkbox was the only thing asking for thirty days.
   */
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await authRequest('sign-in', form);
      if (result.requires_2fa) {
        // The password was accepted and a code is on its way, but nothing is
        // signed in yet — `/auth/login`'s tokens are parked server-side and
        // only become a session once the code checks out (IN-332).
        //
        // `remember` rides along in the URL rather than being applied here,
        // because the choice is only meaningful once the second factor is
        // cleared. `channel` is what the API says it actually used, so the
        // next screen can say "by WhatsApp" when that is the truth.
        const query = new URLSearchParams({
          email: result.email,
          next,
          remember: String(remember),
          flow: SIGNIN_FLOW,
          channel: result.channel || 'email',
        });
        router.push(`/auth/verify?${query}`);
      } else {
        // Only reachable in demo mode, which signs in without a challenge.
        // A consumer token cannot act for a business yet, so the picker
        // lists memberships and mints the scoped session token.
        router.push(`/auth/business-picker?next=${encodeURIComponent(next)}`);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell mode="sign-in">
      <div className="mb-8 flex flex-col gap-2">
        <h1 className="text-5xl font-semibold tracking-tight text-ink">Welcome, Inverser!</h1>
        <p className="text-sm leading-6 text-muted">Sign in to your InversePay account.</p>
      </div>

      <form className="flex flex-col gap-5" onSubmit={submit}>
        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
          required
        />
        <Input
          label="Password"
          name="password"
          type="password"
          showPasswordToggle
          autoComplete="current-password"
          placeholder="Enter password"
          value={form.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
          required
        />

        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-ink">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
              className="size-4 rounded border-line accent-brand"
            />
            Remember this device
          </label>
          <Link href="/auth/forgot-password" className="font-semibold text-brand hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={loading} className="h-12 rounded-xl">
          Login
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted">
        New to InversePay?{' '}
        <Link href="/auth/sign-up" className="font-semibold text-brand hover:underline">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
